import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLang } from '../hooks/useLang';
import { useAuthContext } from '../hooks/useAuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import Navbar from '../components/common/Navbar';
import Footer from '../components/common/Footer';
import '../styles/pages/Home.scss';

// Assets
import boyJumps from '../assets/images/boy_jumps.png';
import manStands from '../assets/images/man_stands.png';
import imgHandwriting from '../assets/images/handwriting_to_pdf.png';
import imgLecture from '../assets/images/lecture_to_quiz.png';
import imgPlagiarism from '../assets/images/plagiarism_checker.png';
import imgAiDetector from '../assets/images/ai_detector.png';
import imgSummarizer from '../assets/images/summarizer.png';

const SPONSORS = [
    'crypto.com', 'Podium', 'FINOM', 'RYANAIR', 'amenitiz', 'fever', 'F A I R E'
];

const AI_INTEGRATIONS = [
    {
        title: 'Handwriting to PDF (LaTeX)',
        description: 'Convert your handwritten notes into perfectly formatted LaTeX PDFs. Our AI preserves symbols and complex formulas.',
        image: imgHandwriting,
        color: '#4F46E5',
        icon: 'fa-pen-nib'
    },
    {
        title: 'Lecture to Quiz',
        description: 'Transform any lecture recording or text into interactive study quizzes. Instant flashcards and practice tests.',
        image: imgLecture,
        color: '#059669',
        icon: 'fa-lightbulb'
    },
    {
        title: 'Plagiarism Checker',
        description: 'Ensure originality with our deep-scanning plagiarism detection. Compare against millions of academic sources.',
        image: imgPlagiarism,
        color: '#DC2626',
        icon: 'fa-shield-check'
    },
    {
        title: 'AI Detector',
        description: 'Verify content authenticity with advanced linguistic pattern matching. Stay ahead with AI-generated text detection.',
        image: imgAiDetector,
        color: '#7C3AED',
        icon: 'fa-robot'
    },
    {
        title: 'Summarizer',
        description: 'Distill long documents into concise, structured summaries instantly. Get the key takeaways in seconds.',
        image: imgSummarizer,
        color: '#D97706',
        icon: 'fa-align-left'
    },
];

const Home = () => {
    const { language } = useLang();
    const { user } = useAuthContext();
    const navigate = useNavigate();
    const location = useLocation();
    const [currentSlide, setCurrentSlide] = React.useState(0);

    React.useEffect(() => {
        const timer = setInterval(() => {
            setCurrentSlide((prev) => (prev + 1) % AI_INTEGRATIONS.length);
        }, 5000);
        return () => clearInterval(timer);
    }, []);

    React.useEffect(() => {
        if (!location.hash) return;
        const target = document.getElementById(location.hash.slice(1));
        if (target) {
            const timer = setTimeout(() => target.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
            return () => clearTimeout(timer);
        }
    }, [location.hash]);

    if (user) {
        return (
            <div className="home-wrapper">
                <Navbar />
                <main className="user-home-section">
                    <motion.div
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.8 }}
                        className="welcome-card"
                    >
                        <h1>Explore YeniDers</h1>
                        <p>Discover notes, join communities, and use our AI tools to boost your learning.</p>
                        <div className="user-cta-grid">
                            <button onClick={() => navigate(`/${language}/library`)}>Browse Library</button>
                            {/* <button onClick={() => navigate(`/${language}/community`)}>Community</button> */}
                        </div>
                    </motion.div>
                </main>
            </div>
        );
    }

    return (
        <div className="home-wrapper">
            {/* The transparent Navbar overlays the beautiful background */}
            <Navbar />

            <div className="home-scroll">
            {/* Hero Section */}
            <main className="hero-section">
                <div className="hero-content">
                    <motion.h1
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, ease: "easeOut" }}
                    >
                        Learn, Share, and Grow Together
                    </motion.h1>

                    <motion.p
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.2, ease: "easeOut" }}
                    >
                        YeniDers is the ultimate community for students and teachers. Upload your notes, discover new opportunities, and accelerate your learning journey today.
                    </motion.p>

                    <motion.div
                        className="email-cta"
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.4, ease: "easeOut" }}
                    >
                        <div className="input-with-button">
                            <i className="fa-solid fa-magnifying-glass search-icon"></i>
                            <input type="text" placeholder="Search notes, courses, or users..." />
                            <button className="cta-button" onClick={() => navigate(`/${language}/login`)}>Search &rarr;</button>
                        </div>
                    </motion.div>
                </div>

                {/* Right Side Image (floating animation placeholder) */}
                <motion.div
                    className="hero-image-container"
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 1, delay: 0.3, ease: "easeOut" }}
                >
                    <motion.img
                        src={boyJumps}
                        alt="Boy jumps"
                        className="floating-boy"
                        animate={{ y: [0, -20, 0] }}
                        transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                    />
                </motion.div>
            </main>

            {/* Infinite scrolling sponsors */}
            {/* <div className="sponsors-marquee">
                <ul>
                    {SPONSORS.map((sponsor, index) => (
                        <li key={index}>
                            <span>{sponsor}</span>
                        </li>
                    ))}
                </ul>
                <ul aria-hidden="true">
                    {SPONSORS.map((sponsor, index) => (
                        <li key={`dup-${index}`}>
                            <span>{sponsor}</span>
                        </li>
                    ))}
                </ul>
            </div> */}

            {/* Features Section */}
            <section id="features" className="features-section">
              <div className="features-card">
                <div className="features-left">
                    <motion.h2
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.8, ease: "easeOut" }}
                    >
                        Share your Notes
                    </motion.h2>
                    <motion.p
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.8, delay: 0.2, ease: "easeOut" }}
                    >
                        We convert uploaded notes & scanned textbooks in any language or subject into perfectly structured, searchable PDFs.
                    </motion.p>
                    <motion.ul
                        className="features-list"
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.8, delay: 0.3, ease: "easeOut" }}
                    >
                        <li>
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="check-icon"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path><polyline points="22 4 12 14.01 9 11.01"></polyline></svg>
                            AI extracts chapters automatically
                        </li>
                        <li>
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="check-icon"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path><polyline points="22 4 12 14.01 9 11.01"></polyline></svg>
                            Formulas & charts are preserved
                        </li>
                        <li>
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="check-icon"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path><polyline points="22 4 12 14.01 9 11.01"></polyline></svg>
                            High-quality, searchable PDF output
                        </li>
                        <li>
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="check-icon"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path><polyline points="22 4 12 14.01 9 11.01"></polyline></svg>
                            Multilingual and multi-subject support
                        </li>
                    </motion.ul>
                    <motion.button
                        className="signup-button"
                        onClick={() => navigate(`/${language}/signup`)}
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.8, delay: 0.4, ease: "easeOut" }}
                    >
                        Create Account
                    </motion.button>
                </div>

                <div className="features-right">
                    <motion.div
                        className="upload-box"
                        onClick={() => navigate(`/${language}/login`)}
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.8, delay: 0.2, ease: "easeOut" }}
                    >
                        <div className="upload-icon-circle">
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path><polyline points="17 8 12 3 7 8"></polyline><line x1="12" y1="3" x2="12" y2="15"></line></svg>
                        </div>
                        <p className="upload-text"><strong>Drop files here or click to upload</strong></p>
                        <p className="upload-subtext">PDF, JPG, PNG up to 50MB</p>
                    </motion.div>

                    <motion.div
                        className="uploaded-files-list"
                        initial={{ opacity: 0, x: 30 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.8, delay: 0.4, ease: "easeOut" }}
                    >
                        <div className="file-item">
                            <i className="fa-regular fa-file-lines file-icon"></i>
                            <span>Chapter 1: Introduction</span>
                        </div>
                        <div className="file-item">
                            <i className="fa-regular fa-file-lines file-icon"></i>
                            <span>Chapter 2: Derivatives</span>
                        </div>
                        <div className="file-item">
                            <i className="fa-regular fa-file-lines file-icon"></i>
                            <span>Quiz: 10 questions</span>
                        </div>
                    </motion.div>

                    <motion.img
                        src={manStands}
                        alt="Man stands"
                        className="features-image"
                        initial={{ opacity: 0, scale: 0.8 }}
                        whileInView={{ opacity: 1, scale: 1 }}
                        viewport={{ once: true }}
                        transition={{ duration: 1, delay: 0.6, ease: "easeOut" }}
                    />
                </div>
              </div>
            </section>

            {/* AI Integrations Slider */}
            <section id="ai-tools" className="ai-slider-section">
                <div className="slider-wrapper">
                    <motion.div
                        className="slider-header"
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                    >
                        <h2>Explore Our AI Tools</h2>
                        <p className="slider-subtitle">From summarizing notes to generating quizzes, here's what YeniDers AI can do for you.</p>
                    </motion.div>

                    <div className="slider-main">
                        <div className="slider-left-content">
                            <AnimatePresence mode="wait">
                                <motion.div
                                    key={currentSlide}
                                    className="content-slide"
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: -20 }}
                                    transition={{ duration: 0.5 }}
                                >
                                    <div className="item-icon" style={{ color: AI_INTEGRATIONS[currentSlide].color }}>
                                        <i className={`fa-solid ${AI_INTEGRATIONS[currentSlide].icon}`}></i>
                                    </div>
                                    <h3 style={{ color: AI_INTEGRATIONS[currentSlide].color }}>{AI_INTEGRATIONS[currentSlide].title}</h3>
                                    <p>{AI_INTEGRATIONS[currentSlide].description}</p>
                                    <button className="learn-more-btn" style={{ borderColor: AI_INTEGRATIONS[currentSlide].color, color: AI_INTEGRATIONS[currentSlide].color }}>
                                        Explore Feature
                                    </button>
                                </motion.div>
                            </AnimatePresence>

                            <div className="slider-nav">
                                {AI_INTEGRATIONS.map((_, index) => (
                                    <div
                                        key={index}
                                        className={`nav-dot ${index === currentSlide ? 'active' : ''}`}
                                        onClick={() => setCurrentSlide(index)}
                                        style={{ backgroundColor: index === currentSlide ? AI_INTEGRATIONS[index].color : 'rgba(255,255,255,0.2)' }}
                                    >
                                        {index === currentSlide && (
                                            <div
                                                key={currentSlide}
                                                className="dot-timer"
                                                style={{
                                                    animationDuration: '5s',
                                                    backgroundColor: AI_INTEGRATIONS[index].color
                                                }}
                                            />
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="slider-right-visual">
                            <AnimatePresence mode="wait">
                                <motion.div
                                    key={currentSlide}
                                    className="visual-slide"
                                    initial={{ opacity: 0, scale: 0.8, rotate: -5, x: 50 }}
                                    animate={{ opacity: 1, scale: 1, rotate: 0, x: 0 }}
                                    exit={{ opacity: 0, scale: 0.9, x: -50 }}
                                    transition={{ duration: 0.8, ease: "circOut" }}
                                >
                                    <div className="image-card" style={{ boxShadow: `0 40px 80px ${AI_INTEGRATIONS[currentSlide].color}33` }}>
                                        <img src={AI_INTEGRATIONS[currentSlide].image} alt={AI_INTEGRATIONS[currentSlide].title} />
                                        <div className="card-overlay" style={{ background: `linear-gradient(to top, ${AI_INTEGRATIONS[currentSlide].color}66, transparent)` }}></div>
                                    </div>

                                    {/* Decorative floating elements */}
                                    <motion.div
                                        className="float-element"
                                        animate={{ y: [0, -20, 0] }}
                                        transition={{ duration: 3, repeat: Infinity }}
                                        style={{ background: AI_INTEGRATIONS[currentSlide].color, top: '10%', right: '10%' }}
                                    />
                                    <motion.div
                                        className="float-element small"
                                        animate={{ y: [0, 15, 0] }}
                                        transition={{ duration: 4, repeat: Infinity }}
                                        style={{ background: AI_INTEGRATIONS[currentSlide].color, bottom: '20%', left: '5%' }}
                                    />
                                </motion.div>
                            </AnimatePresence>
                        </div>
                    </div>
                </div>
            </section>

            <Footer />
            </div>
        </div>
    );
};

export default Home;
